<template>
    <div class="fixed w-full h-full z-50 inset-0 flex flex-col justify-center items-center text-center">
        <h1 class="duration-1000 transition ease-in-out"
            :class="headingClass"
            v-html="heading"/>
        <p class="duration-1000 transition ease-in-out"
           :class="subheadingClass"
           v-html="subheading"/>
        <div v-if="isLoading" class="duration-1000 transition ease-in-out" :class="loadingClass" >
            <img alt="Loading…" class="mx-auto animate-spin w-24 h-24 select-none" src="/temp/breezeline_pinwheel_white.png">
        </div>
        <div v-else :class="loadingClass" class="duration-1000 transition ease-in-out">
            <IconCircleExclamation class="text-salmon mx-auto mt-2 text-5xl desktop:text-8xl select-none" />
        </div>
    </div>
</template>

<script>
import { mapActions, mapState, mapGetters } from "vuex"
import IconCircleExclamation from "@/atoms/icons/IconCircleExclamation"
import products from "../../../../templates/products/_products";
import {captureException} from "@sentry/browser";

export default {
    name: "AbandonedCart",
    components: {
        IconCircleExclamation,
    },
    props: {
        productMetadata: {
            type: String,
            required: false,
            default: "[]",
        }
    },
    data: () => ({
        heading: 'Welcome Back!',
        headingClass: 'opacity-0',
        subheading: 'Please wait while we retrieve your cart...',
        subheadingClass: 'opacity-0',
        loadingClass: 'opacity-0',
        isLoading: true,
    }),
    computed: {
        ...mapState([
            'meta',
            'residentialOffers'
        ]),
        ...mapGetters({
            filterAndMerge: 'filterAndMerge',
            parseMetadata: 'parseMetadata'
        }),
        productData () {
            return {
                file: this.parseMetadata(products ?? []),
                craft: this.parseMetadata(JSON.parse(this.productMetadata ?? "[]"))
            }
        },
        mergedMetadata() {
            return this.filterAndMerge({base: this.productData.file, override: this.productData.craft})
        },
        residentialOffers() {
            return this.$store.state.residentialOffers;
        }
    },
    mounted() {
        setTimeout(() => this.headingClass = "opacity-100", 500)
        setTimeout(() => {
            this.subheadingClass = "opacity-100"
            this.loadingClass = "opacity-100"
            this.retrieveSession()
        }, 1000)
    },
    methods:{
        ...mapActions({
            setMeta: 'setMeta',
            setLabels: 'setLabels',
            initialize: 'Buyflow/initialize',
            setSelectedOffer: 'setSelectedOffer',
            reinitializeSession: 'reinitializeSession',
            setLoadingVisibilty: 'setLoadingVisibilty',
            retrieveUserSession: 'Buyflow/retrieveUserSession'
        }),
        ...mapActions('BroadbandLabels', [
            'savePendingLabelRequestGroup',
            'savePendingLabelRequest'
        ]),
        getSpeeds(serviceName) {
            if (!serviceName || !this.mergedMetadata || Object.keys(this.mergedMetadata).length === 0) {
                return { download: null, upload: null };
            }
            
            try {
                const data = this.mergedMetadata[serviceName]?.[0];
                const internetProduct = data?.variants?.customizations?.internet?.products?.[serviceName];
                
                if (!internetProduct) {
                    return { download: null, upload: null };
                }
                
                const download = internetProduct.download;
                const upload = internetProduct.upload;
                
                return {
                    download: download || null,
                    upload: upload || null
                };

            } catch (error) {
                console.error('Error getting speeds:', error);
                captureException(error);
                return { download: null, upload: null };
            }
        },
        getPromoDuration(serviceName) {
            if (!serviceName || !this.mergedMetadata || Object.keys(this.mergedMetadata).length === 0) {
                    return "12";
            }
                
            try {
                const data = this.mergedMetadata[serviceName]?.[0];
                
                if (!data?.variants?.customizations?.internet?.products?.[serviceName]?.promoMonths) {
                    return "12";
                }
                
                const duration = data.variants.customizations.internet.products[serviceName].promoMonths.split(',')[0];
               
                return duration;

            } catch (error) {
                    console.error('Error getting promoDuration:', error);
                    captureException(error);
                    return "12";
            }
        },
        getInstallationInfo(matchingOffer) {
            const defaultInstall = "Professional Installation";

            if (!matchingOffer?.variants?.install) {
                return { price: 0, title: defaultInstall };
            }
            
            try {
                // Find the preferred installation option
                const preferredInstall = matchingOffer.variants.install.find(
                    installOption => installOption.preferred === installOption.name
                );
                
                if (preferredInstall) {
                    return {
                        price: preferredInstall.fullPrice || 0,
                        title: preferredInstall.name === "Professional Install" ? defaultInstall : preferredInstall.name 
                    };
                }
                
                // no preferred found, look for "Professional Install"
                const professionalInstall = matchingOffer.variants.install.find(
                    installOption => installOption.name === "Professional Install"
                );
                
                if (professionalInstall) {
                    return {
                        price: professionalInstall.fullPrice || 0,
                        title: professionalInstall.name === "Professional Install" ? defaultInstall : professionalInstall.name
                    };
                }
                
                // fallback: return first installation option
                const firstInstall = matchingOffer.variants.install[0];
                return {
                    price: firstInstall?.fullPrice || 0,
                    title: firstInstall?.title || firstInstall?.name || defaultInstall
                };
                
            } catch (error) {
                console.error('Error getting installation info:', error);
                captureException(error);
                return { price: 0, title: defaultInstall };
            }
        },
        toCent(amount) {
            if (typeof amount === 'string') {
                amount = parseFloat(amount);
            }
            
            if (typeof amount !== 'number' || isNaN(amount)) {
                console.error('Invalid input to toCent function:', amount);
                return 0;
            }

            return Math.round(amount * 100);
        },
        paperStatementFee(stateOrProvince) {
            const states = [
                {"state": "CT", "price": "0.00"},
                {"state": "DE", "price": "3.00"},
                {"state": "FL", "price": "3.00"},
                {"state": "MA", "price": "0.00"},
                {"state": "MD", "price": "3.00"},
                {"state": "ME", "price": "0.00"},
                {"state": "NH", "price": "3.00"},
                {"state": "NY", "price": "0.00"},
                {"state": "OH", "price": "3.00"},
                {"state": "PA", "price": "3.00"},
                {"state": "SC", "price": "3.00"},
                {"state": "VA", "price": "3.00"},
                {"state": "WV", "price": "0.00"}
            ];
            const result = states.find(item => item.state === stateOrProvince);
            return result ? result.price : "0.00";
        },
        async saveBroadbandLabelRequest(selected) {
            const selectedProductName = selected['HS1 HSD Service'].name;
            if (!this.residentialOffers || this.residentialOffers.length === 0) {
                console.error('No residential offers available');
                return;
            }
                    
            try {
                const pageLang = document.querySelector("html")?.getAttribute("lang") || 'en';
                const language = pageLang === "es" ? "spanish" : "english";
                
                let labelRequests = [];
                let selectedRequest = null;
                
                for (const offer of this.residentialOffers) {
                    const promoPrice = offer.promoPrice;
                    const fullPrice = offer.fullPrice;
                    const promoDuration = this.getPromoDuration(offer.name);
                    const { download, upload } = this.getSpeeds(offer.name);
                    const gatewayPrice = offer.variants.equipment.equipment_gateway[0].fullPrice;
                    const extenderPrice = offer.variants.equipment.equipment_wyw[0].variants.equipment_wyw_pods[0].fullPrice;
                    const { price: installationFee, title: installationTitle } = this.getInstallationInfo(offer);
                    
                    const oneTimeFees = {
                        installationType: [
                            {
                                title: installationTitle,
                                value: this.toCent(installationFee)
                            }
                        ]
                    };

                    const payload = {
                        planName: offer.name,
                        tier: download.toString(),
                        downloadSpeed: download * 1000,
                        uploadSpeed: upload * 1000,
                        promotionalPriceDuration: promoDuration,
                        price: this.toCent(fullPrice),
                        promotionalPrice: this.toCent(promoPrice),
                        offerId: this.meta.offerId.toString(),
                        marketCode: this.meta.marketType,
                        hasPromotionalPrice: !!(offer.promoPrice && offer.promoPrice > 0),
                        language: language,
                        additionalCharges: {
                            providerMonthlyFees: {
                                equipmentLease: {
                                    title: "Equipment Lease",
                                    value: this.toCent(gatewayPrice)
                                },
                                wifiExtender: {
                                    title: "Optional WiFi Extender",
                                    value: this.toCent(extenderPrice)
                                },
                                paperStatementFee: {
                                    title: "Paper Statement Fee",
                                    value: this.toCent(this.paperStatementFee(this.meta.stateOrProvince))
                                }
                            },
                            oneTimeFees: oneTimeFees
                        }
                    };

                    labelRequests.push(payload);

                    if (offer.name === selectedProductName) {
                        selectedRequest = payload;
                    }
                }

                // Save to store
                if (labelRequests.length > 0) {
                    await this.savePendingLabelRequestGroup(labelRequests);
                    await this.savePendingLabelRequest(selectedRequest.planName);
                } 
                
            } catch (error) {
                console.error('Error preparing broadband label requests:', error);
                captureException(error);
            }
        },
        retrieveSession() {
            if (this.$route.query.uuid) {
                this.setMeta({ uuid: this.$route.query.uuid,})
                this.setLoadingVisibilty({ value: false})

                this.retrieveUserSession()
                    .then(r => {
                        let data = r.data.data

                        let selected;
                        this.changeMessage(null,'We found your cart and are loading it up now...', true)

                        if (data?.decisions !== null) {
                            selected = data.decisions
                        } else { // todo remove this else block once all carts are migrated to the new abandoned cart format (wait at least 1 month after launch)
                            const tvType = data.ve1TivoExperience && JSON.parse(data.ve1TivoExperience).indexOf('Stream') === -1 ? ["VE1 TiVo Experience", "VE2 Additional Equipment"]: ["VE1 Stream TV", "VE2 Additional Stream TV Equipment"]
                            selected = {
                                ...(data.hs1HsdService          && {"HS1 HSD Service":           JSON.parse(data.hs1HsdService)}),
                                ...(data.he1ModemPlume          && {"HE1 Modem & Plume":         JSON.parse(data.he1ModemPlume)}),
                                ...(data.he2AdditionalPods      && {"HE2 Additional Pods":       JSON.parse(data.he2AdditionalPods)}),
                                ...(data.he3WifiYourWay         && {"HE3 - WYW":                 JSON.parse(data.he3WifiYourWay)}),
                                ...(data.ps1PhoneService        && {"PS1 Phone Service":         JSON.parse(data.ps1PhoneService)}),
                                ...(data.ve1TivoExperience      && {[tvType[0]]:                 JSON.parse(data.ve1TivoExperience)}),
                                ...(data.ve2AdditionalEquipment && {[tvType[1]]:                 JSON.parse(data.ve2AdditionalEquipment)}),
                                ...(data.vs2AdditionalChannels  && {"VS2 Additional Channels":   JSON.parse(data.vs2AdditionalChannels)}),
                                ...(data.ss1StreamingServices   && {"SS1 Streaming Services":    JSON.parse(data.ss1StreamingServices)}),
                                ...(data.if1InstallFulfillment  && {"IF1 Install - Fulfillment": JSON.parse(data.if1InstallFulfillment)})
                            }
                        }

                        this.setSelectedOffer({
                            selectedOffer: {
                                selected,
                            },
                        })

                        this.reinitializeSession({
                            firstName: data.firstName || "",
                            lastName: data.lastName || "",
                            emailAddress: data.emailAddress || "",
                            phoneNumber: data.phoneNumber || "",
                            locationId: data.locationId,
                            billerMappingCode: data.billerMappingCode,
                            address: {
                                line1: data.line1,
                                line2: data.line2,
                                city: data.city,
                                stateOrProvince: data.stateOrProvince,
                                postalCode: data.postalCode.substring(0, 5),
                            }
                        })
                        .then(() => {
                            if (this.meta.isCurrentCustomer) throw 'already a customer'
                           
                            //else
                                this.saveBroadbandLabelRequest(selected);

                            // Redirect 
                            setTimeout(() => {
                                console.log('checkout redirect');
                                window.location = '/checkout';
                            }, 500);
                        })
                        .catch(error => {
                            console.error(error)
                            if (error === 'offer selection error') {
                                this.changeMessage(null,'Part of your cart was not able to fully load...', true)
                                window.location = '/checkout'
                            } else {
                                this.changeMessage(
                                    'Oh No!',
                                        (error === 'already a customer') ?
                                            'It looks like you are already a customer<br>you will be redirected in a few seconds...' :
                                            'We ran into issues loading your cart<br>you will be redirected in a few seconds...',
                                    false
                                )
                            }
                            setTimeout(() => {
                                window.location = '/internet'
                            }, 2500)
                        })
                    })
                    .catch(error => {
                        console.error(error)
                        this.changeMessage(
                            'Oh No!',
                            'We could not locate your cart in our system<br>you will be redirected in a few seconds...',
                            false
                        )

                        setTimeout(() => {
                            window.location = '/internet'
                        }, 2500)
                    })
            } else {
                this.changeMessage('Oh No!', 'You have not provided a uuid<br>you will be redirected in a few seconds...', false)

                setTimeout(() => {
                    window.location = '/internet'
                }, 2500)
            }
            this.setLoadingVisibilty(true)
        },
        changeMessage(heading, subheading, loading){
            setTimeout(() => {
                (heading)? this.headingClass = "opacity-0": null;
                (subheading)? this.subheadingClass = "opacity-0": null;
                (!loading)? this.loadingClass = "opacity-0": null;
                setTimeout(() => {
                    this.isLoading = loading
                    this.heading = (heading)? heading: this.heading;
                    this.subheading = (subheading)? subheading: this.subheading;
                }, 1000)
                setTimeout(() => {
                    this.headingClass = "opacity-100"
                    this.subheadingClass = "opacity-100"
                    this.loadingClass = "opacity-100"
                }, 1200)
            }, 1500)
        },
    }
}
</script>
